import { useMemo } from "react";
import {
  ChartBaseType,
  DateDataPoint,
  Dates,
  ForecastedCostRe,
  ResourceExplorerChartData,
} from "@/services/cloudchipr.api";
import { billingExplorerChartDataLabelsGenerator } from "@/components/pages/common/billing-explorer/utils/billingExplorerChartDataLabelsGenerator";
import { billingExplorerChartDataGenerator } from "@/components/pages/common/billing-explorer/utils/billingExplorerChartDataGenerator";
import { aggregateResourceExplorerPercentBasedChartData } from "@/store/resource-explorer/utils/helpers/chart/aggregateResourceExplorerPercentBasedChartData";
import { aggregateResourceExplorerChartDataSeriesTotalCostsMap } from "@/store/resource-explorer/utils/helpers/chart/aggregateResourceExplorerChartDataSeriesTotalCostsMap";
import { aggregateHighChartDataSeriesMap } from "@/storybook/c8r-highcharts/common/utils/helpers/aggregateHighChartDataSeriesMap";
import { aggregateResourceExplorerPercentBasedChartDataSeriesMap } from "@/store/resource-explorer/utils/helpers/chart/aggregateResourceExplorerPercentBasedChartDataSeriesMap";
import { addForecastedCostIntoBillingExplorerChartData } from "@/components/pages/common/billing-explorer/utils/addForecastedCostIntoBillingExplorerChartData";

export const useBillingExplorerWidgetChartDataHook = (
  data?: ResourceExplorerChartData[],
  chartBase?: ChartBaseType,
  dateDataPoint?: DateDataPoint | null,
  quarterStartMonth: number = 1,
  display_forecast?: boolean,
  forecastedCosts?: ForecastedCostRe,
  dates?: Dates,
) => {
  const labels = useMemo(() => {
    if (!dateDataPoint || !data) {
      return;
    }

    return billingExplorerChartDataLabelsGenerator(
      data,
      dateDataPoint,
      quarterStartMonth,
    );
  }, [data, dateDataPoint, quarterStartMonth]);

  let chartDataCostBased = useMemo(() => {
    if (!labels || !data) {
      return;
    }

    return billingExplorerChartDataGenerator(data, labels);
  }, [data, labels]);

  const needToAddForecast =
    display_forecast &&
    dateDataPoint === "daily" &&
    chartBase === "cost" &&
    forecastedCosts;

  if (
    chartDataCostBased &&
    needToAddForecast &&
    (dates?.label === "month_to_date" || dates?.label === "quarter_to_date")
  ) {
    const { chartDataWithForecast } =
      addForecastedCostIntoBillingExplorerChartData(
        chartDataCostBased,
        dates.label,
        dateDataPoint,
        labels ?? null,
        forecastedCosts,
        quarterStartMonth,
      );
    chartDataCostBased = chartDataWithForecast ?? chartDataCostBased;
  }

  const chartDataPercentBased = useMemo(() => {
    if (!chartDataCostBased) {
      return;
    }

    return aggregateResourceExplorerPercentBasedChartData(chartDataCostBased);
  }, [chartDataCostBased]);

  const totalCostsMap = useMemo(
    () => aggregateResourceExplorerChartDataSeriesTotalCostsMap(data ?? []),
    [data],
  );

  const chartDataCostBasedMap = useMemo(() => {
    if (!chartDataCostBased) {
      return;
    }

    return aggregateHighChartDataSeriesMap(chartDataCostBased);
  }, [chartDataCostBased]);

  const chartDataPercentBasedMap = useMemo(() => {
    if (!chartDataPercentBased) {
      return;
    }

    return aggregateResourceExplorerPercentBasedChartDataSeriesMap(
      chartDataPercentBased,
    );
  }, [chartDataPercentBased]);

  const chartData =
    chartBase === "percent" ? chartDataPercentBased : chartDataCostBased;

  return {
    chartData,
    chartDataCostBasedMap,
    chartDataPercentBasedMap,
    totalCostsMap,
  };
};
